import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, agentsAPI } from '../services/api';

const TEAM_BLANK = { name: '', description: '' };
const SETTINGS_BLANK = { dispatch_mode: 'manual', max_concurrent_appointments: 1, auto_assign_new_leads: true };

// Simple password generator (readable, no ambiguous chars)
function genPassword(len = 10) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

function TeamModal({ team, onSave, onClose, allAgents }) {
  const [form, setForm] = useState(team || TEAM_BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [assignedIds, setAssignedIds] = useState([]);
  const [availableAgents, setAvailableAgents] = useState([]);

  useEffect(() => {
    if (team?.id) {
      adminAPI.getTeam(team.id).then(d => {
        if (d?.agents) setAssignedIds(d.agents.map(a => a.id));
      }).catch(() => {});
    }
    // Filter agents not already in another team (simplified)
    agentsAPI.list().then(agents => {
      setAvailableAgents((agents || []).filter(a => a.role === 'agent' && a.active === 1));
    }).catch(() => {});
  }, [team?.id]);

  const toggleAgent = (aid) => setAssignedIds(prev =>
    prev.includes(aid) ? prev.filter(id => id !== aid) : [...prev, aid]
  );

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const payload = { name: form.name, description: form.description };
      if (team?.id) {
        await adminAPI.updateTeam(team.id, payload);
        // Sync agents
        const current = await adminAPI.getTeam(team.id).then(d => d.agents?.map(a => a.id) || []).catch(() => []);
        const toAdd = assignedIds.filter(id => !current.includes(id));
        const toRemove = current.filter(id => !assignedIds.includes(id));
        for (const id of toAdd) await adminAPI.addAgentToTeam(team.id, id);
        for (const id of toRemove) await adminAPI.removeAgentFromTeam(team.id, id);
      } else {
        const r = await adminAPI.createTeam(payload);
        for (const id of assignedIds) await adminAPI.addAgentToTeam(r.id, id);
      }
      onSave();
    } catch (err) {
      setError(err.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 700 }}>
        <div className="modal-header">
          <h3 className="modal-title">{team ? 'Edit Team' : 'New Team'}</h3>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Team Name *</label>
                <input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="input" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--bg-primary)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 2 }}>👥 Team Members</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>Assign agents to this team</div>
              {availableAgents.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>No available agents</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflow: 'auto' }}>
                  {availableAgents.map(a => (
                    <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 12px', borderRadius: 8, background: assignedIds.includes(a.id) ? 'rgba(124,58,237,0.08)' : 'var(--bg-secondary)', border: `2px solid ${assignedIds.includes(a.id) ? 'var(--purple)' : 'var(--border)'}` }}>
                      <input type="checkbox" checked={assignedIds.includes(a.id)} onChange={() => toggleAgent(a.id)} style={{ width: 16, height: 16, accentColor: 'var(--purple)' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{a.full_name || a.username}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{a.extension || '—'} · {a.email || '—'}</div>
                      </div>
                      {assignedIds.includes(a.id) && <span style={{ fontSize: 12, color: 'var(--purple)', fontWeight: 700 }}>✓</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SettingsModal({ teamId, onClose }) {
  const [settings, setSettings] = useState(SETTINGS_BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    adminAPI.getTeamSettings(teamId).then(d => {
      if (d) setSettings({
        dispatch_mode: d.dispatch_mode,
        max_concurrent_appointments: d.max_concurrent_appointments,
        auto_assign_new_leads: d.auto_assign_new_leads === 1
      });
    }).catch(() => {});
  }, [teamId]);

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await adminAPI.updateTeamSettings(teamId, {
        dispatch_mode: settings.dispatch_mode,
        max_concurrent_appointments: settings.max_concurrent_appointments,
        auto_assign_new_leads: settings.auto_assign_new_leads ? 1 : 0
      });
      onClose();
    } catch (err) {
      setError(err.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h3 className="modal-title">⚙️ Dispatch Settings</h3>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group">
              <label className="form-label">Dispatch Mode</label>
              <select className="select" value={settings.dispatch_mode} onChange={e => setSettings(s => ({ ...s, dispatch_mode: e.target.value }))}>
                <option value="manual">📋 Manual — supervisor assigns each appointment</option>
                <option value="auto_round_robin">🔄 Auto Round-Robin — distribute evenly to available agents</option>
              </select>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                {settings.dispatch_mode === 'manual'
                  ? 'You manually assign each appointment from the queue to an agent.'
                  : 'Appointments are automatically assigned to the next available agent in the team.'}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Max Concurrent Appointments per Agent</label>
              <input className="input" type="number" min="1" max="10" value={settings.max_concurrent_appointments}
                onChange={e => setSettings(s => ({ ...s, max_concurrent_appointments: parseInt(e.target.value) || 1 }))} />
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                How many active appointments an agent can have at once.
              </div>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={settings.auto_assign_new_leads}
                  onChange={e => setSettings(s => ({ ...s, auto_assign_new_leads: e.target.checked }))} />
                Auto-assign new leads to team queue
              </label>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, marginLeft: 24 }}>
                When enabled, new leads matching team criteria go straight to the queue.
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function QueueModal({ teamId, onClose }) {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamAgents, setTeamAgents] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [q, t] = await Promise.all([
          adminAPI.getTeamQueue(teamId),
          adminAPI.getTeam(teamId)
        ]);
        setQueue(q || []);
        setTeamAgents(t?.agents || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [teamId]);

  const handleAssign = async (queueId, agentId) => {
    try {
      await adminAPI.assignQueueItem(teamId, queueId, agentId);
      setQueue(prev => prev.map(i => i.id === queueId ? { ...i, status: 'assigned', assigned_agent_id: agentId } : i));
    } catch (err) { alert(err.error || 'Assign failed'); }
  };

  const handleComplete = async (queueId) => {
    try {
      await adminAPI.completeQueueItem(teamId, queueId);
      setQueue(prev => prev.map(i => i.id === queueId ? { ...i, status: 'completed' } : i));
    } catch (err) { alert(err.error || 'Complete failed'); }
  };

  const handleAutoAssign = async () => {
    try {
      const r = await adminAPI.autoAssignQueue(teamId);
      setQueue(prev => prev.map(i => i.id === r.queueId ? { ...i, status: 'assigned', assigned_agent_id: r.agent.id } : i));
    } catch (err) { alert(err.error || 'Auto-assign failed'); }
  };

  if (loading) return <div className="modal-overlay"><div className="modal"><div style={{ padding: 40, textAlign: 'center' }}>Loading...</div></div></div>;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 900 }}>
        <div className="modal-header">
          <h3 className="modal-title">📋 Appointment Queue</h3>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: 600, overflow: 'auto' }}>
          {queue.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>No appointments in queue</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Lead</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Phone</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Assigned Agent</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {queue.map(item => (
                  <tr key={item.id} style={{ borderBottom: '1px solid var(--border-dim)' }}>
                    <td style={{ padding: '8px 12px' }}>{item.first_name} {item.last_name}</td>
                    <td style={{ padding: '8px 12px', fontFamily: 'var(--mono)' }}>{item.phone}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                        background: item.status === 'pending' ? 'rgba(245,158,11,0.15)' :
                                  item.status === 'assigned' ? 'rgba(59,130,246,0.15)' :
                                  item.status === 'completed' ? 'rgba(16,185,129,0.15)' :
                                  'rgba(239,68,68,0.15)',
                        color: item.status === 'pending' ? 'var(--orange)' :
                               item.status === 'assigned' ? 'var(--blue)' :
                               item.status === 'completed' ? 'var(--green)' : 'var(--red)'
                      }}>
                        {item.status.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>{item.agent_name || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      {item.status === 'pending' && (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <select className="select" style={{ width: 140 }} defaultValue="" onChange={e => e.target.value && handleAssign(item.id, parseInt(e.target.value))}>
                            <option value="">Assign to...</option>
                            {teamAgents.filter(a => a.active).map(a => (
                              <option key={a.id} value={a.id}>{a.full_name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {item.status === 'assigned' && (
                        <button className="btn btn-sm btn-success" onClick={() => handleComplete(item.id)}>Complete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {queue.some(i => i.status === 'pending') && (
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <button className="btn btn-primary" onClick={handleAutoAssign}>🔄 Auto-Assign Next</button>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [settingsModal, setSettingsModal] = useState(null);
  const [queueModal, setQueueModal] = useState(null);
  const [allAgents, setAllAgents] = useState([]);

  const load = useCallback(async () => {
    try {
      const [t, a] = await Promise.all([
        adminAPI.listTeams(),
        agentsAPI.list()
      ]);
      setTeams(t || []);
      setAllAgents(a || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this team and remove all members?')) return;
    await adminAPI.deleteTeam(id).catch(e => alert(e.error));
    load();
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Teams</h1>
          <p className="page-subtitle">{teams.length} teams configured</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal({})}>+ New Team</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Team</th>
                <th>Supervisor</th>
                <th>Members</th>
                <th>Dispatch Mode</th>
                <th>Queue</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {teams.map(t => (
                <tr key={t.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{t.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.description || '—'}</div>
                  </td>
                  <td>{t.supervisor_id}</td>
                  <td className="mono">{t.agent_count || 0}</td>
                  <td>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      background: t.dispatch_mode === 'auto_round_robin' ? 'rgba(124,58,237,0.15)' : 'rgba(245,158,11,0.15)',
                      color: t.dispatch_mode === 'auto_round_robin' ? 'var(--purple)' : 'var(--orange)'
                    }}>
                      {t.dispatch_mode === 'auto_round_robin' ? 'Auto Round-Robin' : 'Manual'}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-sm btn-secondary" onClick={() => setQueueModal(t.id)}>
                      View Queue
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button className="btn btn-sm btn-secondary" onClick={() => setSettingsModal(t.id)}>⚙️ Settings</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setModal(t)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(t.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal !== null && (
        <TeamModal
          team={modal.id ? modal : null}
          onSave={() => { setModal(null); load(); }}
          onClose={() => setModal(null)}
          allAgents={allAgents}
        />
      )}

      {settingsModal && (
        <SettingsModal
          teamId={settingsModal}
          onClose={() => { setSettingsModal(null); load(); }}
        />
      )}

      {queueModal && (
        <QueueModal
          teamId={queueModal}
          onClose={() => setQueueModal(null)}
        />
      )}
    </div>
  );
}